/**
 * A catalogue fixture as `.m-row` in the prototype's modals.css draws it.
 *
 * The sibling of `pages/contests/edit/fixtures/attached-fixture-row.tsx`: the
 * same card, over the other payload. That one renders a `ContestDetailFixture`
 * already on the contest and offers rename and detach; this one renders a
 * `CatalogueFixture` from the feed and offers selection. The card itself —
 * border, hover, the head's geometry, the expand band — is `FixtureCard`, which
 * both compose.
 *
 * Here rather than beside one screen because two of them draw it over the same
 * payload: the Add Fixtures modal (CAE-1551, which built it) and the Catalogue
 * (CAE-1555). Leaving it under `edit/fixtures/` would have had the Catalogue
 * reaching into a sibling screen's folder for a row that is not that screen's
 * any more.
 *
 * > **The move does not finish the layering.** It still imports
 * > `formatFixtureStatusLabel`, `FixtureMetaClauses` and `crestTeam` /
 * > `toFixturePillState` from three folders under `pages/contests/`, so a
 * > shared component depends on one screen's tree. That is not new — the
 * > `FixtureCard` it composes already imported `FixtureMetaClauses` the same
 * > way — and unpicking it means moving those three modules and their six
 * > other callers, which is its own change rather than a rider on this one.
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { Theme } from '@mui/material/styles';
import type { CatalogueFixture } from '../../api/catalogue';
import { FixtureCard, InfoIcon, MarketBadge, Popover, StatusPill } from '../ui';
import { InfoPanel } from './info-panel';
import { formatSportLabel } from '../../utils/sport-label';
import { formatFixtureStatusLabel } from '../../pages/contests/detail-tabs/contest-detail-tab-utils';
import { FixtureMetaClauses } from '../../pages/contests/fixture-meta-clauses';
import { crestTeam, toFixturePillState } from '../../pages/contests/overview/fixture-display';

/**
 * What the row calls a fixture.
 *
 * `localisedName` first: the catalogue's own translation of the event name is
 * what the Catalogue screen shows, and a modal that renamed every row relative
 * to the screen it mirrors would not be the same list. The display-name
 * override lives on `contest_fixtures` and does not exist yet for a fixture
 * nobody has attached, so there is nothing of `resolveFixtureLabel`'s to apply
 * here.
 */
export const catalogueFixtureLabel = (fixture: CatalogueFixture): string =>
  fixture.localisedName?.trim() || fixture.name;

export interface CatalogueFixtureRowProps {
  fixture: CatalogueFixture;
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /**
   * Already on the contest. The row stays visible and readable — an operator
   * building a selection needs to see that the fixture they are looking for is
   * already there — but it cannot be selected, and the API would drop it
   * anyway.
   *
   * Only the Add Fixtures modal has an answer for this. The Catalogue is not
   * contest-scoped, so no fixture it lists is "already added" to anything.
   */
  attached?: boolean;
  /**
   * Offer the fixture's own ids beside its title — the restyled
   * `FixtureInfoPopover` the old Catalogue page carried on every row.
   *
   * The Catalogue alone, the same way the drill-down's rename is. CAE-1555 asks
   * for the three info popovers to survive the restructure, and this is the one
   * about the fixture; the Add Fixtures modal is a picker, where the event ref
   * an operator is reconciling against Caesars is not the question being asked.
   *
   * Mutually exclusive with `attached` in practice — the Catalogue is not
   * contest-scoped, so no row it draws is attached to anything — which is why
   * both can share the card's one title slot.
   */
  showDetails?: boolean;
  children?: React.ReactNode;
}

export const CatalogueFixtureRow = ({
  fixture,
  selected,
  onSelectedChange,
  expanded,
  onExpandedChange,
  attached = false,
  showDetails = false,
  children,
}: CatalogueFixtureRowProps) => {
  const label = catalogueFixtureLabel(fixture);

  return (
    <FixtureCard
      testId={`catalogue-fixture-${fixture.externalId}`}
      home={crestTeam(fixture.homeTeam || label)}
      away={fixture.awayTeam ? crestTeam(fixture.awayTeam) : undefined}
      title={label}
      selection={{
        selected: selected || attached,
        onSelectedChange,
        label: `Select ${label}`,
        disabled: attached,
      }}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      expandLabel={label}
      meta={<FixtureMetaClauses fixture={fixture} kickoffTracking="-0.01em" />}
      titleAdornment={
        attached ? (
          <AlreadyAddedTag />
        ) : showDetails ? (
          <FixtureInfoButton fixture={fixture} />
        ) : undefined
      }
      side={
        <>
          <StatusPill
            state={toFixturePillState(fixture.status)}
            size="sm"
            label={formatFixtureStatusLabel(fixture.status)}
          />
          {/*
           * No `queued` state. `MarketBadge` draws one, and the prototype's
           * foot grows a batched "Sync markets for N fixtures" button to reach
           * it — but both screens that mount this row sync from inside the
           * expanded row and show the result there, so nothing here is ever
           * waiting on a sync it did not already report. Logged in
           * prototype/README.md.
           */}
          <MarketBadge marketCount={fixture.marketCount} outcomeCount={fixture.outcomeCount} />
        </>
      }
    >
      {children}
    </FixtureCard>
  );
};

/**
 * `FixtureInfoPopover`, restyled — the event's own refs, for reconciling a row
 * against what Caesars holds.
 *
 * Revealed on hover and on focus, through `FixtureCard`'s own
 * `data-row-affordance` rule. That rule is scoped to one card, and there is
 * exactly one of these per card, so it reveals what the pointer is actually
 * over — unlike the market rename, which needed its own narrower rule because
 * a card can hold 141 markets.
 *
 * The ticket's row sketch lists checkbox · crests · title · pills · chevron and
 * no info control; its acceptance criteria ask for all three popovers to
 * survive. Hover-revealing it honours both — the row reads as drawn until the
 * operator goes looking for the ids.
 */
const FixtureInfoButton = ({ fixture }: { fixture: CatalogueFixture }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const label = catalogueFixtureLabel(fixture);

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Details for ${label}`}
        data-row-affordance
        onClick={(event) => {
          // The head toggles the row open; asking for the ids is not opening.
          event.stopPropagation();
          // Read before the updater, for the reason the market panel gives.
          const anchor = event.currentTarget;
          setAnchorEl((previous) => (previous ? null : anchor));
        }}
        sx={(theme: Theme) => ({
          flexShrink: 0,
          width: '26px',
          height: '26px',
          opacity: 0,
          transition: 'opacity 0.12s',
          borderRadius: `${theme.tokens.radius.idChip}px`,
          color: theme.tokens.color.ink4,
          '&:hover': { backgroundColor: theme.tokens.color.bg, color: theme.tokens.color.ink2 },
        })}
      >
        <InfoIcon sx={{ fontSize: '12px' }} />
      </IconButton>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        label="Fixture details"
        onClose={() => setAnchorEl(null)}
      >
        {/* Stopped for the reason the market panel stops it — the synthetic
            event would otherwise reach the row head and expand it. */}
        <Box onClick={(event) => event.stopPropagation()}>
          <InfoPanel
            eyebrow="Fixture"
            title={label}
            rows={[
              { key: 'Event ID', value: fixture.externalId, mono: true, truncate: true },
              { key: 'Home', value: fixture.homeTeam || '—', truncate: true },
              { key: 'Away', value: fixture.awayTeam || '—', truncate: true },
              { key: 'Competition', value: fixture.competitionName ?? '—', truncate: true },
              ...(fixture.competitionId
                ? [
                    {
                      key: 'Competition ref',
                      value: fixture.competitionId,
                      mono: true,
                      truncate: true,
                    },
                  ]
                : []),
              {
                key: 'Sport',
                value: fixture.sportCode
                  ? (formatSportLabel(fixture.sportCode) ?? fixture.sportCode)
                  : '—',
              },
            ]}
          />
        </Box>
      </Popover>
    </>
  );
};

/**
 * `.m-already` — the Add Outcomes modal's "Already added" tag, borrowed here
 * for the same claim about a fixture.
 *
 * Beside the title rather than in the side slot: the side already carries the
 * status pill and the market badge, both of which are facts about the feed, and
 * this is a fact about *this contest*. It belongs with the name it qualifies.
 */
const AlreadyAddedTag = () => (
  <Box
    component="span"
    sx={(theme: Theme) => ({
      flexShrink: 0,
      fontSize: '11px',
      fontWeight: 500,
      letterSpacing: '0.01em',
      color: theme.tokens.color.ink4,
    })}
  >
    Already added
  </Box>
);
